import { ApolloError, useMutation } from '@apollo/client';

import { logTapestryEvent } from 'logger';

import {
  CancelTapestryJob,
  CancelTapestryJobVariables,
  ConfirmTapestryJob,
  ConfirmTapestryJobVariables,
  RejectTapestryJob,
  RejectTapestryJobVariables,
  RetryTapestryJob,
  RetryTapestryJobVariables,
} from './__generated__/operations';
import {
  CANCEL_TAPESTRY_JOB,
  CONFIRM_TAPESTRY_JOB,
  REJECT_TAPESTRY_JOB,
  RETRY_TAPESTRY_JOB,
} from './operations';

/**
 * Pull the api error code (e.g. `E-4093`, `E-4030`) off a thrown mutation
 * error so callers can map it to a specific message. Tolerant of both
 * ApolloError and the looser shape some callers catch.
 */
export function extractTapestryErrorCode(error: unknown): string | undefined {
  if (error instanceof ApolloError) {
    return error.graphQLErrors[0]?.extensions?.code as string | undefined;
  }
  if (error && typeof error === 'object' && 'graphQLErrors' in error) {
    const errs = (error as { graphQLErrors?: Array<{ extensions?: { code?: string } }> })
      .graphQLErrors;
    return errs?.[0]?.extensions?.code;
  }
  return undefined;
}

export interface UseTapestryJobActionsResult {
  confirm: (fileId: string) => Promise<unknown>;
  reject: (fileId: string) => Promise<unknown>;
  /** Retry a failed run (pipeline_failed). */
  retry: (fileId: string) => Promise<unknown>;
  /** Cancel an in-progress run. */
  cancel: (fileId: string) => Promise<unknown>;
  loading: boolean;
}

/**
 * Lifecycle mutations for the Tapestry run. `confirm` requires the file to be
 * READY_TO_PERSIST (else the api returns `E-4093`); `reject` aborts at the
 * review gate; `retry` restarts a failed run; `cancel` stops an in-progress
 * run. All take only the file id.
 */
export function useTapestryJobActions(): UseTapestryJobActionsResult {
  const [confirmMutation, { loading: confirming }] = useMutation<
    ConfirmTapestryJob,
    ConfirmTapestryJobVariables
  >(CONFIRM_TAPESTRY_JOB);
  const [rejectMutation, { loading: rejecting }] = useMutation<
    RejectTapestryJob,
    RejectTapestryJobVariables
  >(REJECT_TAPESTRY_JOB);
  const [retryMutation, { loading: retrying }] = useMutation<
    RetryTapestryJob,
    RetryTapestryJobVariables
  >(RETRY_TAPESTRY_JOB);
  const [cancelMutation, { loading: cancelling }] = useMutation<
    CancelTapestryJob,
    CancelTapestryJobVariables
  >(CANCEL_TAPESTRY_JOB);

  // Emit one TapestryAction event per lifecycle mutation (latency + success/
  // error + api error code) so the gates' success rates and failure modes are
  // observable without instrumenting each call site.
  const run = async (action: string, fileId: string, mutate: () => Promise<unknown>) => {
    const start = performance.now();
    try {
      const result = await mutate();
      logTapestryEvent(action, { fileId, status: 'success' }, performance.now() - start);
      return result;
    } catch (error) {
      logTapestryEvent(
        action,
        { fileId, status: 'error', errorCode: extractTapestryErrorCode(error) ?? 'unknown' },
        performance.now() - start,
      );
      throw error;
    }
  };

  return {
    confirm: (fileId: string) =>
      run('confirm', fileId, () => confirmMutation({ variables: { fileId } })),
    reject: (fileId: string) =>
      run('reject', fileId, () => rejectMutation({ variables: { fileId } })),
    retry: (fileId: string) => run('retry', fileId, () => retryMutation({ variables: { fileId } })),
    cancel: (fileId: string) =>
      run('cancel', fileId, () => cancelMutation({ variables: { fileId } })),
    loading: confirming || rejecting || retrying || cancelling,
  };
}
